<template>
    <ion-page>
        <standard-header title="Picks" align="middle"/>
        <ion-content :fullscreen="true">
            <pull-down-refresh :callback="refresh"/>
            <skeleton :rows="4" v-if="shipments === null"/>
            <sub-title v-if="shipments !== null" :count="shipments?shipments.length:null">My Picks</sub-title>
            <ion-list>
                <ion-item>
                    <ion-label>Filter by Drop Off</ion-label>
                    <ion-select v-model="currentZoneId">
                        <ion-select-option :value="null">All</ion-select-option>
                        <ion-select-option :value="zone.id" v-for="zone in zones" :key="zone.id">
                            {{ zone.name }}
                        </ion-select-option>
                    </ion-select>
                </ion-item>
                <div v-for="shipment in filteredShipments" :key="shipment.id">
                    <ion-item detail button @click.stop="goToShipment(shipment)" lines="none">
                        <pick-list-item :shipment="shipment" :completed="shipment.is_picked"/>
                        <shipment-membership-tools
                            type="shipments"
                            stage="pick"
                            name="pick"
                            @updated="refresh()"
                            ref="members"
                            :job="shipment"
                            :users="shipment.pickers"
                            v-if="!shipment.picked_at"
                        />
                    </ion-item>
                    <ion-progress-bar :value="shipment.pc_complete/100"/>
                </div>
            </ion-list>
            <div v-show="filteredShipments && filteredShipments.length < 1">
                <no-results title="No Results">
                    No picks have been assigned to you
                </no-results>
            </div>
        </ion-content>
    </ion-page>
</template>

<script>
import _, {map} from 'lodash';
import {alertController, IonContent, IonItem, IonList, IonPage, IonProgressBar, IonSelect, IonSelectOption, pickerController} from '@ionic/vue';
import StandardHeader from "@/components/StandardHeader.vue";
import SubTitle from "@/components/SubTitle";
import NoResults from "@/components/NoResults";
import EventBus from '@/EventBus';
import Skeleton from "@/components/Skeleton";
import PullDownRefresh from "@/components/PullDownRefresh";
import router from '@/router';
import ShipmentMembershipTools from "@/components/ShipmentMembershipTools";
import PrioritySorter from "@/services/PrioritySorter";
import PickListItem from "@/views/modules/picking/components/PickListItem";

export default {
    inject: ['$http', '$validationAlert', '$toast'],
    name: 'MyPicksList',
    components: {
        PickListItem,
        ShipmentMembershipTools,
        PullDownRefresh,
        Skeleton,
        NoResults,
        SubTitle,
        StandardHeader,
        IonContent, IonPage, IonItem, IonList, IonProgressBar,
        IonSelect, IonSelectOption
    },
    data() {
        return {
            zones: [],
            currentZoneId: null,
            shipments: null,
        }
    },
    computed: {
        filteredShipments() {
            if (!this.currentZoneId) {
                return this.shipments;
            }
            return _.filter(this.shipments, shipment => {
                return shipment.picking_drop_off_zone_id === this.currentZoneId;
            });
        }
    },
    methods: {
        ionViewDidEnter() {
            this.refresh();
        },
        /**
         * Fetch all the bays from the API, then fire up the picker.
         * If they choose a new value, POST and set the drop off.
         * @returns {Promise<void>}
         */
        async showDropOffAlert(shipment) {
            EventBus().emitter.emit('loading', true);
            const zones = await this.$http.get('/api/locations/zones?search=is_bay:1').then(response => {
                return response.data.items;
            }).catch(error => {
                this.$validationAlert(error);
            }).finally(() => {
                EventBus().emitter.emit('loading', false);
            });

            const picker = await pickerController.create({
                buttons: [{text: 'Cancel', role: 'cancel'}, {text: 'Confirm'}],
                columns: [{
                    name: 'zone',
                    options: map(zones, zone => {
                        return {
                            value: zone.id,
                            text: zone.name
                        }
                    })
                }]
            });
            await picker.present();
            picker.onDidDismiss().then(value => {
                if (value.data && value.data.zone) {
                    EventBus().emitter.emit('loading', true);
                    const payload = {zone: {id: value.data.zone.value}};
                    const url = `/api/shipments/${shipment.id}/pick-drop-off`;
                    this.$http.post(url, payload).then(() => {
                        this.refresh();
                        this.$toast.success('Drop off location set');
                    }).catch(error => {
                        this.$validationAlert(error);
                    }).finally(() => {
                        EventBus().emitter.emit('loading', false);
                    });
                }
            });
        },
        goToShipment(shipment) {

            if (shipment.picked_at) {
                let message = 'Shipment has already been picked.';
                if (shipment.picking_drop_off_zone_id) {
                    message += ' Shipment is at ' + shipment.picking_drop_off_zone.name;
                } else {
                    message += ' No drop off set yet';
                }
                alertController.create({
                    message: message,
                    buttons: [{
                        text: 'Set Drop Off',
                        handler: async () => {
                            this.showDropOffAlert(shipment);
                        }
                    }, 'Close']
                }).then(alert => alert.present());
                return;
            }

            if (this.$refs.members.hasAcceptedMembership(shipment.pickers)) {
                router.push({name: 'picks.pick', params: {id: shipment.id}});
            } else {
                alertController.create({
                    message: 'You have not yet accepted this job',
                    buttons: ['Ok']
                }).then(alert => alert.present());
            }
        },
        refresh(event = null) {
            this.$http.get('/api/locations/zones?search=is_bay:1').then(response => {
                this.zones = response.data.items;
            });
            EventBus().emitter.emit('loading', true);
            this.$http.get('/api/picks/me/shipments', {
                params: {include: 'pickers,picking_drop_off_zone', all: true}
            }).then(response => {
                this.shipments = PrioritySorter.sortByPriorityThen(response.data.items, 'reference');
            }).finally(() => {
                if (event && event.target.complete) {
                    event.target.complete();
                }
                EventBus().emitter.emit('loading', false);
            });
        }
    }
}
</script>
